// File: src/components/Maintenance/MaintenanceManagementTaskPage.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const safeArray = (v) =>
  Array.isArray(v)
    ? v
    : Array.isArray(v?.items)
      ? v.items
      : Array.isArray(v?.data)
        ? v.data
        : Array.isArray(v?.vendors)
          ? v.vendors
          : Array.isArray(v?.assets)
            ? v.assets
            : Array.isArray(v?.staff)
              ? v.staff
              : [];

const COST_SOURCE_OPTIONS = [
  { value: "manual", label: "Manual" },
  { value: "po", label: "Purchase Order" },
  { value: "vendor_invoice", label: "Vendor Invoice" },
  { value: "internal_labour", label: "Internal Labour" },
  { value: "adjustment", label: "Adjustment" },
];

const COST_TYPE_OPTIONS = [
  { value: "callout", label: "Callout" },
  { value: "parts", label: "Parts" },
  { value: "labour", label: "Labour" },
  { value: "travel", label: "Travel" },
  { value: "inspection", label: "Inspection" },
  { value: "invoice", label: "Invoice" },
  { value: "other", label: "Other" },
];

const createRowId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const emptyPoLine = () => ({
  row_id: createRowId(),
  item_id: "",
  item_code: "",
  item_name: "",
  unit_size: "",
  quantity: "1",
  unit_price: "",
  tax_rate: "20",
});

const formatMoney = (value) => {
  const n = Number(value);
  return Number.isFinite(n)
    ? new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n)
    : "£0.00";
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toPositiveNumberOrNull = (value) => {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const toNumberOrNull = (value) => {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const statusBadgeClass = (status) => {
  const s = String(status || "").toLowerCase();

  if (["closed", "completed"].includes(s)) {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }

  if (["assigned", "approved", "in progress"].includes(s)) {
    return "border-blue-200 bg-blue-50 text-blue-700";
  }

  if (["waiting parts", "waiting vendor", "on hold"].includes(s)) {
    return "border-amber-200 bg-amber-50 text-amber-800";
  }

  if (["submitted", "under review"].includes(s)) {
    return "border-violet-200 bg-violet-50 text-violet-700";
  }

  if (["cancelled"].includes(s)) {
    return "border-slate-200 bg-slate-100 text-slate-700";
  }

  return "border-rose-200 bg-rose-50 text-rose-700";
};

const priorityBadgeClass = (priority) => {
  const p = String(priority || "").toLowerCase();

  if (p === "high") return "border-rose-200 bg-rose-50 text-rose-700";
  if (p === "medium") return "border-amber-200 bg-amber-50 text-amber-800";
  return "border-slate-200 bg-slate-100 text-slate-700";
};

const Card = ({ title, subtitle, rightSlot, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {rightSlot ? <div>{rightSlot}</div> : null}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const InfoTile = ({ label, value }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className="mt-1 break-words text-sm font-medium text-slate-900">
      {value ?? "—"}
    </div>
  </div>
);

const EmptyState = ({ text }) => (
  <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
    {text}
  </div>
);

const buildStaffLabel = (staff) => {
  if (!staff) return "—";

  return (
    staff.label ||
    [
      staff.full_name || `Staff #${staff.staff_id || staff.id || "?"}`,
      staff.position || null,
      staff.department_name || staff.department || null,
    ]
      .filter(Boolean)
      .join(" — ")
  );
};

const buildVendorLabel = (vendor) => {
  if (!vendor) return "—";
  return vendor.vendor_name || vendor.name || `Vendor #${vendor.vendor_id || vendor.id || "?"}`;
};

const getLineTotal = (line) => {
  const qty = Number(line?.quantity || 0);
  const price = Number(line?.unit_price || 0);
  return Number.isFinite(qty) && Number.isFinite(price) ? qty * price : 0;
};

const getNormalizedPoLink = (poId) => `/procurement/po-details/${poId}`;

export default function MaintenanceManagementTaskPage() {
  const navigate = useNavigate();
  const { taskId } = useParams();

  const numericTaskId = Number(taskId);

  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);

  const [staffOptions, setStaffOptions] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffSearch, setStaffSearch] = useState("");
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [selectedStaffPreview, setSelectedStaffPreview] = useState(null);

  const [assetSearch, setAssetSearch] = useState("");
  const [assetResults, setAssetResults] = useState([]);
  const [assetSearchLoading, setAssetSearchLoading] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState("");
  const [selectedAssetPreview, setSelectedAssetPreview] = useState(null);

  const [assignNote, setAssignNote] = useState("");

  const [linkPoId, setLinkPoId] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [calloutRef, setCalloutRef] = useState("");
  const [vendorDispatchNote, setVendorDispatchNote] = useState("");

  const [costForm, setCostForm] = useState({
    source_type: "manual",
    cost_type: "parts",
    vendor_id: "",
    po_id: "",
    description: "",
    quantity: "1",
    unit_cost: "",
    amount: "",
    note: "",
    incurred_at: "",
  });

  const [closeNote, setCloseNote] = useState("");
  const [closeCost, setCloseCost] = useState("");

  const [procurementContext, setProcurementContext] = useState(null);
  const [procurementContextLoading, setProcurementContextLoading] = useState(false);
  const [linkedPurchaseOrders, setLinkedPurchaseOrders] = useState([]);
  const [linkedPurchaseOrdersLoading, setLinkedPurchaseOrdersLoading] = useState(false);

  const [procurementVendorId, setProcurementVendorId] = useState("");
  const [procurementExpectedDelivery, setProcurementExpectedDelivery] = useState("");
  const [procurementNotes, setProcurementNotes] = useState("");
  const [procurementPlannerId, setProcurementPlannerId] = useState("");
  const [procurementLocationId, setProcurementLocationId] = useState("");

  const [stockSearch, setStockSearch] = useState("");
  const [stockResults, setStockResults] = useState([]);
  const [stockSearchLoading, setStockSearchLoading] = useState(false);

  const [poLines, setPoLines] = useState([emptyPoLine()]);

  const [actionLoading, setActionLoading] = useState({
    assign: false,
    vendorDispatch: false,
    close: false,
    linkAsset: false,
    addCost: false,
    createPo: false,
    procurementLinkPo: false,
  });

  const overviewTask = overview?.task || null;
  const comments = useMemo(() => safeArray(overview?.comments), [overview?.comments]);
  const assignments = useMemo(() => safeArray(overview?.assignments), [overview?.assignments]);
  const costs = useMemo(() => safeArray(overview?.costs), [overview?.costs]);

  const linkedAsset = useMemo(() => {
    return (
      overview?.linked_asset ||
      overview?.asset ||
      overviewTask?.asset ||
      overviewTask?.asset_details ||
      (overviewTask?.asset_id
        ? {
            asset_id: overviewTask.asset_id,
            asset_number: overviewTask.asset_number,
            asset_name: overviewTask.asset_name,
            location_text: overviewTask.location,
            asset_type: overviewTask.asset_type,
            status: overviewTask.asset_status,
          }
        : null)
    );
  }, [overview, overviewTask]);

  const procurementPoTotal = useMemo(() => {
    return poLines.reduce((sum, line) => sum + getLineTotal(line), 0);
  }, [poLines]);

  const loadOverview = useCallback(async () => {
    if (!Number.isInteger(numericTaskId) || numericTaskId <= 0) {
      setOverview(null);
      setOverviewLoading(false);
      return;
    }

    try {
      setOverviewLoading(true);
      const { data } = await api.get(
        `/solo-mdsh-api/maintenance/management/${numericTaskId}/overview`
      );

      setOverview(data);

      const task = data?.task || null;

      if (task?.vendor_id) {
        setVendorId(String(task.vendor_id));
        setCostForm((prev) => ({
          ...prev,
          vendor_id: prev.vendor_id || String(task.vendor_id),
        }));
      }

      if (task?.related_po_id) {
        setLinkPoId(String(task.related_po_id));
        setCostForm((prev) => ({
          ...prev,
          po_id: prev.po_id || String(task.related_po_id),
        }));
      }
    } catch (err) {
      console.error("Failed to load task overview:", err);
      toast.error(err?.response?.data?.error || "Failed to load task overview");
      setOverview(null);
    } finally {
      setOverviewLoading(false);
    }
  }, [numericTaskId]);

  const fetchProcurementContext = useCallback(async () => {
    if (!Number.isInteger(numericTaskId) || numericTaskId <= 0) return;

    try {
      setProcurementContextLoading(true);

      const { data } = await api.get(
        `/solo-mdsh-api/maintenance/procurement/tasks/${numericTaskId}/context`
      );

      setProcurementContext(data);

      const defaults = data?.defaults || {};
      if (defaults.vendor_id) {
        setProcurementVendorId(String(defaults.vendor_id));
      }
      if (defaults.notes) {
        setProcurementNotes(defaults.notes);
      } else if (data?.task) {
        setProcurementNotes((prev) => prev || "");
      }
      if (defaults.related_po_id) {
        setLinkPoId((prev) => prev || String(defaults.related_po_id));
      }
    } catch (err) {
      console.error("Failed to load procurement context:", err);
      setProcurementContext(null);
    } finally {
      setProcurementContextLoading(false);
    }
  }, [numericTaskId]);

  const fetchLinkedPurchaseOrders = useCallback(async () => {
    if (!Number.isInteger(numericTaskId) || numericTaskId <= 0) return;

    try {
      setLinkedPurchaseOrdersLoading(true);

      const { data } = await api.get(
        `/solo-mdsh-api/maintenance/procurement/tasks/${numericTaskId}/purchase-orders`
      );

      setLinkedPurchaseOrders(safeArray(data?.items || data));
    } catch (err) {
      console.error("Failed to load linked purchase orders:", err);
      setLinkedPurchaseOrders([]);
    } finally {
      setLinkedPurchaseOrdersLoading(false);
    }
  }, [numericTaskId]);

  const fetchVendors = useCallback(async () => {
    try {
      setVendorsLoading(true);
      const { data } = await api.get("/solo-mdsh-api/maintenance/procurement/vendors");
      setVendors(safeArray(data));
    } catch (err) {
      console.error("Failed to load vendors:", err);
      setVendors([]);
    } finally {
      setVendorsLoading(false);
    }
  }, []);

  const fetchStaff = useCallback(
    async (query = "") => {
      try {
        setStaffLoading(true);

        const { data } = await api.get("/solo-mdsh-api/maintenance/management/staff", {
          params: {
            q: query.trim() || undefined,
            limit: 100,
          },
        });

        const items = safeArray(data);
        setStaffOptions(items);

        if (selectedStaffId) {
          const found = items.find(
            (item) => String(item.staff_id || item.id) === String(selectedStaffId)
          );
          if (found) {
            setSelectedStaffPreview(found);
          }
        }
      } catch (err) {
        console.error("Failed to load staff:", err);
        setStaffOptions([]);
      } finally {
        setStaffLoading(false);
      }
    },
    [selectedStaffId]
  );

  const searchAssets = useCallback(async (query) => {
    const q = String(query || "").trim();

    if (!q || q.length < 2) {
      setAssetResults([]);
      return;
    }

    try {
      setAssetSearchLoading(true);

      const { data } = await api.get("/solo-mdsh-api/maintenance/management/assets/search", {
        params: { q },
      });

      setAssetResults(safeArray(data));
    } catch (err) {
      console.error("Failed to search assets:", err);
      toast.error("Failed to search assets");
      setAssetResults([]);
    } finally {
      setAssetSearchLoading(false);
    }
  }, []);

  const searchStockItems = useCallback(
    async (query, vendorValue) => {
      const q = String(query || "").trim();
      const selectedVendor = String(vendorValue || procurementVendorId || "").trim();

      if (!selectedVendor || !q || q.length < 2) {
        setStockResults([]);
        return;
      }

      try {
        setStockSearchLoading(true);

        const { data } = await api.get("/solo-mdsh-api/maintenance/procurement/stock-items", {
          params: {
            q,
            vendor_id: Number(selectedVendor),
            limit: 50,
          },
        });

        setStockResults(safeArray(data));
      } catch (err) {
        console.error("Failed to search stock items:", err);
        setStockResults([]);
      } finally {
        setStockSearchLoading(false);
      }
    },
    [procurementVendorId]
  );

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    fetchProcurementContext();
    fetchLinkedPurchaseOrders();
  }, [fetchProcurementContext, fetchLinkedPurchaseOrders]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  useEffect(() => {
    fetchStaff("");
  }, [fetchStaff]);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchStaff(staffSearch);
    }, 300);

    return () => clearTimeout(t);
  }, [staffSearch, fetchStaff]);

  useEffect(() => {
    const t = setTimeout(() => {
      searchAssets(assetSearch);
    }, 350);

    return () => clearTimeout(t);
  }, [assetSearch, searchAssets]);

  useEffect(() => {
    const t = setTimeout(() => {
      searchStockItems(stockSearch, procurementVendorId);
    }, 350);

    return () => clearTimeout(t);
  }, [stockSearch, procurementVendorId, searchStockItems]);

  const refreshPageData = useCallback(async () => {
    await Promise.all([
      loadOverview(),
      fetchProcurementContext(),
      fetchLinkedPurchaseOrders(),
    ]);
  }, [loadOverview, fetchProcurementContext, fetchLinkedPurchaseOrders]);

  const handleAssign = async () => {
    if (!numericTaskId || !selectedStaffId) {
      toast.error("Choose a staff member first");
      return;
    }

    try {
      setActionLoading((prev) => ({ ...prev, assign: true }));

      await api.post(`/solo-mdsh-api/maintenance/management/${numericTaskId}/assign`, {
        assigned_to: Number(selectedStaffId),
        department_id: selectedStaffPreview?.department_id || null,
        assignment_note: assignNote || null,
      });

      toast.success("Task assigned");
      setSelectedStaffId("");
      setSelectedStaffPreview(null);
      setAssignNote("");
      await refreshPageData();
    } catch (err) {
      console.error("Assignment failed:", err);
      toast.error(err?.response?.data?.error || "Assignment failed");
    } finally {
      setActionLoading((prev) => ({ ...prev, assign: false }));
    }
  };

  const handleLinkPO = async () => {
    const poId = toPositiveNumberOrNull(linkPoId);

    if (!numericTaskId || !poId) {
      toast.error("Enter a valid purchase order ID");
      return;
    }

    try {
      setActionLoading((prev) => ({ ...prev, procurementLinkPo: true }));

      await api.post(`/solo-mdsh-api/maintenance/procurement/tasks/${numericTaskId}/link-po`, {
        po_id: poId,
      });

      toast.success("Purchase order linked");
      setCostForm((prev) => ({ ...prev, po_id: String(poId) }));
      setLinkPoId("");
      await refreshPageData();
    } catch (err) {
      console.error("PO link failed:", err);
      toast.error(err?.response?.data?.error || "Failed to link purchase order");
    } finally {
      setActionLoading((prev) => ({ ...prev, procurementLinkPo: false }));
    }
  };

  const handleVendorDispatch = async () => {
    if (!numericTaskId) {
      toast.error("Invalid task");
      return;
    }

    try {
      setActionLoading((prev) => ({ ...prev, vendorDispatch: true }));

      await api.post(`/solo-mdsh-api/maintenance/management/${numericTaskId}/vendor-dispatch`, {
        vendor_id: toPositiveNumberOrNull(vendorId),
        callout_ref: calloutRef || null,
        dispatch_note: vendorDispatchNote || null,
      });

      toast.success("Vendor dispatch recorded");
      setCalloutRef("");
      setVendorDispatchNote("");
      await refreshPageData();
    } catch (err) {
      console.error("Vendor dispatch failed:", err);
      toast.error(err?.response?.data?.error || "Failed to record vendor dispatch");
    } finally {
      setActionLoading((prev) => ({ ...prev, vendorDispatch: false }));
    }
  };

  const handleLinkAsset = async () => {
    const assetId = toPositiveNumberOrNull(selectedAssetId);

    if (!numericTaskId || !assetId) {
      toast.error("Choose an asset first");
      return;
    }

    try {
      setActionLoading((prev) => ({ ...prev, linkAsset: true }));

      await api.post(`/solo-mdsh-api/maintenance/management/${numericTaskId}/link-asset`, {
        asset_id: assetId,
      });

      toast.success("Asset linked to task");
      setSelectedAssetId("");
      setSelectedAssetPreview(null);
      setAssetSearch("");
      setAssetResults([]);
      await refreshPageData();
    } catch (err) {
      console.error("Asset link failed:", err);
      toast.error(err?.response?.data?.error || "Failed to link asset");
    } finally {
      setActionLoading((prev) => ({ ...prev, linkAsset: false }));
    }
  };

  const handleCostInputChange = (field, value) => {
    setCostForm((prev) => {
      const next = { ...prev, [field]: value };

      const qty = Number(next.quantity || 0);
      const unitCost = Number(next.unit_cost || 0);

      if (
        (field === "quantity" || field === "unit_cost") &&
        Number.isFinite(qty) &&
        Number.isFinite(unitCost)
      ) {
        next.amount =
          qty > 0 && unitCost >= 0 ? String((qty * unitCost).toFixed(2)) : next.amount;
      }

      return next;
    });
  };

  const handleAddCostLine = async () => {
    if (!numericTaskId) {
      toast.error("Invalid task");
      return;
    }

    if (!costForm.cost_type || !costForm.source_type) {
      toast.error("Cost source and cost type are required");
      return;
    }

    const amountNumber = Number(costForm.amount);
    if (!Number.isFinite(amountNumber) || amountNumber <= 0) {
      toast.error("Enter a valid cost amount");
      return;
    }

    try {
      setActionLoading((prev) => ({ ...prev, addCost: true }));

      await api.post(`/solo-mdsh-api/maintenance/management/${numericTaskId}/cost-lines`, {
        source_type: costForm.source_type,
        cost_type: costForm.cost_type,
        vendor_id: toPositiveNumberOrNull(costForm.vendor_id),
        po_id: toPositiveNumberOrNull(costForm.po_id),
        description: costForm.description || null,
        quantity: costForm.quantity === "" ? null : toNumberOrNull(costForm.quantity),
        unit_cost: costForm.unit_cost === "" ? null : toNumberOrNull(costForm.unit_cost),
        amount: amountNumber,
        note: costForm.note || null,
        incurred_at: costForm.incurred_at || null,
      });

      toast.success("Cost line added");

      setCostForm((prev) => ({
        ...prev,
        source_type: "manual",
        cost_type: "parts",
        description: "",
        quantity: "1",
        unit_cost: "",
        amount: "",
        note: "",
        incurred_at: "",
      }));

      await refreshPageData();
    } catch (err) {
      console.error("Add cost line failed:", err);
      toast.error(err?.response?.data?.error || "Failed to add cost line");
    } finally {
      setActionLoading((prev) => ({ ...prev, addCost: false }));
    }
  };

  const handleCloseTask = async () => {
    if (!numericTaskId) {
      toast.error("Invalid task");
      return;
    }

    try {
      setActionLoading((prev) => ({ ...prev, close: true }));

      await api.post(`/solo-mdsh-api/maintenance/management/${numericTaskId}/close`, {
        close_note: closeNote || null,
        actual_cost: closeCost === "" ? null : toNumberOrNull(closeCost),
      });

      toast.success("Task closed");
      setCloseNote("");
      setCloseCost("");
      await refreshPageData();
    } catch (err) {
      console.error("Close failed:", err);
      toast.error(err?.response?.data?.error || "Failed to close task");
    } finally {
      setActionLoading((prev) => ({ ...prev, close: false }));
    }
  };

  const handleAddPoLine = () => {
    setPoLines((prev) => [...prev, emptyPoLine()]);
  };

  const handleRemovePoLine = (rowId) => {
    setPoLines((prev) => {
      const next = prev.filter((line) => line.row_id !== rowId);
      return next.length ? next : [emptyPoLine()];
    });
  };

  const handlePoLineChange = (rowId, field, value) => {
    setPoLines((prev) =>
      prev.map((line) =>
        line.row_id === rowId
          ? {
              ...line,
              [field]: value,
            }
          : line
      )
    );
  };

  const handleApplyStockItemToLine = (rowId, stockItem) => {
    if (!stockItem) return;

    setPoLines((prev) =>
      prev.map((line) =>
        line.row_id === rowId
          ? {
              ...line,
              item_id: String(stockItem.item_id || ""),
              item_code: stockItem.item_code || "",
              item_name: stockItem.description || line.item_name || "",
              unit_size: stockItem.unit_size || line.unit_size || "",
            }
          : line
      )
    );

    toast.success(`Applied ${stockItem.item_code || "stock item"} to line`);
  };

  const handleCreatePO = async () => {
    if (!numericTaskId) {
      toast.error("Invalid task");
      return;
    }

    const selectedVendorId = toPositiveNumberOrNull(procurementVendorId);
    if (!selectedVendorId) {
      toast.error("Select a vendor for the purchase order");
      return;
    }

    const sanitizedLines = poLines
      .map((line) => ({
        item_id: toPositiveNumberOrNull(line.item_id),
        item_code: String(line.item_code || "").trim(),
        item_name: String(line.item_name || "").trim(),
        unit_size: String(line.unit_size || "").trim(),
        quantity: line.quantity === "" ? null : toNumberOrNull(line.quantity),
        unit_price: line.unit_price === "" ? null : toNumberOrNull(line.unit_price),
        tax_rate: line.tax_rate === "" ? 20 : toNumberOrNull(line.tax_rate),
      }))
      .filter((line) => line.item_id || line.item_code || line.item_name || line.unit_size);

    if (!sanitizedLines.length) {
      toast.error("Add at least one PO line");
      return;
    }

    const invalidLine = sanitizedLines.find(
      (line) =>
        (!line.item_id && !line.item_code) ||
        !Number.isFinite(line.quantity) ||
        line.quantity <= 0 ||
        !Number.isFinite(line.unit_price) ||
        line.unit_price < 0 ||
        !line.unit_size
    );

    if (invalidLine) {
      toast.error("Each PO line must include item, quantity, unit price, and unit size");
      return;
    }

    try {
      setActionLoading((prev) => ({ ...prev, createPo: true }));

      const { data } = await api.post(
        `/solo-mdsh-api/maintenance/procurement/tasks/${numericTaskId}/create-po`,
        {
          vendor_id: selectedVendorId,
          planner_id: toPositiveNumberOrNull(procurementPlannerId),
          expected_delivery: procurementExpectedDelivery || null,
          location_id: toPositiveNumberOrNull(procurementLocationId),
          notes: procurementNotes || null,
          line_items: sanitizedLines,
        }
      );

      toast.success(
        `Purchase order created${data?.purchase_order?.po_number ? `: ${data.purchase_order.po_number}` : ""}`
      );

      setPoLines([emptyPoLine()]);
      setStockSearch("");
      setStockResults([]);
      setProcurementExpectedDelivery("");
      setProcurementPlannerId("");
      setProcurementLocationId("");
      setLinkPoId("");
      setCostForm((prev) => ({
        ...prev,
        po_id: data?.purchase_order?.po_id ? String(data.purchase_order.po_id) : prev.po_id,
      }));

      await refreshPageData();
    } catch (err) {
      console.error("Create PO failed:", err);
      toast.error(err?.response?.data?.error || "Failed to create purchase order");
    } finally {
      setActionLoading((prev) => ({ ...prev, createPo: false }));
    }
  };

  if (!Number.isInteger(numericTaskId) || numericTaskId <= 0) {
    return (
      <div className="min-h-full bg-slate-50 px-4 py-6 sm:px-6">
        <div className="mx-auto max-w-5xl">
          <Card title="Invalid Task" subtitle="The task id in the URL is not valid.">
            <div className="flex flex-col gap-4">
              <EmptyState text="This task link is invalid." />
              <div>
                <Button
                  onClick={() => navigate("/maintenance/manage")}
                  className="bg-slate-900 text-white hover:bg-slate-800"
                >
                  Back to Management Queue
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-[1700px] space-y-6">
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-5 shadow-sm">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div>
              <div className="inline-flex items-center rounded-full border border-cyan-100 bg-cyan-50 px-3 py-1 text-xs font-medium text-cyan-700">
                Maintenance Task Management
              </div>
              <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900">
                {overviewTask?.task_number || `MT-${numericTaskId}`}
              </h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                Dedicated management workspace for assignment, asset linkage, PO linkage,
                maintenance procurement, vendor handling, cost recording, history review,
                comments, and closure.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/maintenance/manage")}
                className="border-slate-300"
              >
                Back to Queue
              </Button>

              <Button
                type="button"
                onClick={refreshPageData}
                className="bg-slate-900 text-white hover:bg-slate-800"
              >
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {overviewLoading ? (
          <Card title="Loading Task" subtitle="Please wait while task details are loading.">
            <EmptyState text="Loading task overview..." />
          </Card>
        ) : !overviewTask ? (
          <Card
            title="Task Not Found"
            subtitle="The requested maintenance task could not be loaded."
          >
            <div className="flex flex-col gap-4">
              <EmptyState text="Task not found or no longer available." />
              <div>
                <Button
                  onClick={() => navigate("/maintenance/manage")}
                  className="bg-slate-900 text-white hover:bg-slate-800"
                >
                  Back to Management Queue
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.1fr_0.9fr]">
            <div className="space-y-6">
              <Card
                title="Task Overview"
                subtitle="Full task summary for management review."
                rightSlot={
                  <Badge className={`border ${statusBadgeClass(overviewTask?.status)}`}>
                    {overviewTask?.status || "—"}
                  </Badge>
                }
              >
                <div className="space-y-5">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">
                          {overviewTask?.issue_type || "—"}
                        </h3>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                          <span>{overviewTask?.task_number || `MT-${overviewTask?.task_id}`}</span>
                          <span>•</span>
                          <span>{overviewTask?.reported_by_name || "Unknown requester"}</span>
                          <span>•</span>
                          <span>{formatDateTime(overviewTask?.created_at)}</span>
                        </div>
                      </div>

                      <Badge className={`border ${priorityBadgeClass(overviewTask?.priority)}`}>
                        {overviewTask?.priority || "—"}
                      </Badge>
                    </div>

                    <p className="mt-4 whitespace-pre-wrap text-sm leading-6 text-slate-700">
                      {overviewTask?.description || "—"}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    <InfoTile
                      label="Task Number"
                      value={overviewTask?.task_number || `MT-${overviewTask?.task_id}`}
                    />
                    <InfoTile label="Status" value={overviewTask?.status} />
                    <InfoTile label="Priority" value={overviewTask?.priority} />
                    <InfoTile label="Urgency" value={overviewTask?.urgency_level} />
                    <InfoTile label="Requester" value={overviewTask?.reported_by_name} />
                    <InfoTile label="Updated By" value={overviewTask?.updated_by_name} />
                    <InfoTile label="Department" value={overviewTask?.department_name} />
                    <InfoTile label="Cost Centre" value={overviewTask?.cost_centre_name} />
                    <InfoTile
                      label="Location"
                      value={linkedAsset?.location_text || overviewTask?.location}
                    />
                    <InfoTile
                      label="Estimated Cost"
                      value={formatMoney(overviewTask?.cost_estimate)}
                    />
                    <InfoTile
                      label="Actual Cost"
                      value={formatMoney(overviewTask?.actual_cost)}
                    />
                    <InfoTile
                      label="Resolved At"
                      value={formatDateTime(overviewTask?.resolved_at)}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <InfoTile
                      label="Purchase Order"
                      value={overview?.linked_purchase_order?.po_number || overviewTask?.po_number}
                    />
                    <InfoTile
                      label="Vendor"
                      value={
                        overview?.linked_purchase_order?.vendor_name ||
                        overviewTask?.vendor_name ||
                        overview?.linked_purchase_order?.vendor_id ||
                        "—"
                      }
                    />
                  </div>

                  {linkedAsset ? (
                    <div className="rounded-2xl border border-cyan-200 bg-cyan-50 p-4">
                      <div className="text-sm font-semibold text-cyan-900">Linked Asset</div>
                      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <InfoTile label="Asset Number" value={linkedAsset?.asset_number} />
                        <InfoTile label="Asset Name" value={linkedAsset?.asset_name} />
                        <InfoTile label="Asset Type" value={linkedAsset?.asset_type} />
                        <InfoTile label="Status" value={linkedAsset?.status} />
                      </div>
                    </div>
                  ) : (
                    <EmptyState text="No asset linked to this task yet." />
                  )}
                </div>
              </Card>

              <Card title="Linked Purchase Orders" subtitle="POs linked or created from this task.">
                {linkedPurchaseOrdersLoading ? (
                  <EmptyState text="Loading linked purchase orders..." />
                ) : linkedPurchaseOrders.length === 0 ? (
                  <EmptyState text="No linked purchase orders yet." />
                ) : (
                  <div className="space-y-3">
                    {linkedPurchaseOrders.map((row) => (
                      <div
                        key={`${row.po_id}-${row.linked_at || row.created_at || "x"}`}
                        className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <div className="text-sm font-semibold text-slate-900">
                              {row.po_number || `PO #${row.po_id}`}
                            </div>
                            <div className="mt-1 text-xs text-slate-500">
                              Vendor: {row.vendor_name || row.vendor_id || "—"} • Status:{" "}
                              {row.status || "—"}
                            </div>
                            <div className="mt-1 text-xs text-slate-500">
                              Linked: {formatDateTime(row.linked_at || row.created_at)}
                            </div>
                            <div className="mt-2 text-sm text-slate-700">
                              {row.note || "—"}
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              className="border-slate-300"
                              onClick={() => navigate(getNormalizedPoLink(row.po_id))}
                            >
                              Open PO
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <Card title="Assignment History" subtitle="Latest assignment records for this task.">
                {assignments.length === 0 ? (
                  <div className="text-sm text-slate-500">No assignments recorded yet.</div>
                ) : (
                  <div className="space-y-3">
                    {assignments.map((row) => (
                      <div
                        key={
                          row.assignment_id ||
                          `${row.assigned_to}-${row.created_at || row.assigned_at}`
                        }
                        className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div className="text-sm font-semibold text-slate-900">
                            {row.assigned_to_name || row.assigned_to || "—"}
                          </div>
                          <div className="text-xs text-slate-500">
                            {formatDateTime(row.created_at || row.assigned_at)}
                          </div>
                        </div>

                        <div className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-2">
                          <div className="text-sm text-slate-700">
                            <span className="font-medium text-slate-900">Role:</span>{" "}
                            {row.assignment_role || "—"}
                          </div>
                          <div className="text-sm text-slate-700">
                            <span className="font-medium text-slate-900">Type:</span>{" "}
                            {row.assignment_type || "—"}
                          </div>
                          <div className="text-sm text-slate-700">
                            <span className="font-medium text-slate-900">Status:</span>{" "}
                            {row.status || (row.is_active ? "Active" : "Inactive") || "—"}
                          </div>
                          <div className="text-sm text-slate-700">
                            <span className="font-medium text-slate-900">Active:</span>{" "}
                            {row.is_active === undefined ? "—" : row.is_active ? "Yes" : "No"}
                          </div>
                        </div>

                        <div className="mt-2 text-sm text-slate-700">
                          <span className="font-medium text-slate-900">Note:</span>{" "}
                          {row.assignment_note || "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <Card title="Comments" subtitle="Recent task conversation and notes.">
                {comments.length === 0 ? (
                  <div className="text-sm text-slate-500">No comments yet.</div>
                ) : (
                  <div className="space-y-3">
                    {comments.slice(0, 12).map((row) => (
                      <div
                        key={row.comment_id}
                        className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <div className="text-sm leading-6 text-slate-800">
                          {row.comment_text || "—"}
                        </div>
                        <div className="mt-2 text-xs text-slate-500">
                          {row.staff_name || row.full_name || "—"} •{" "}
                          {formatDateTime(row.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <Card
                title="Cost Lines"
                subtitle="Current recorded maintenance costs for this task."
                rightSlot={
                  <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-700">
                    Live add enabled
                  </Badge>
                }
              >
                {costs.length === 0 ? (
                  <div className="text-sm text-slate-500">
                    No maintenance cost lines recorded yet.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {costs.slice(0, 12).map((row) => (
                      <div
                        key={row.id || row.cost_line_id}
                        className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          <div className="text-sm text-slate-700">
                            <span className="font-medium text-slate-900">Type:</span>{" "}
                            {row.type || row.cost_type || "—"}
                          </div>
                          <div className="text-sm text-slate-700">
                            <span className="font-medium text-slate-900">Created:</span>{" "}
                            {formatDateTime(row.created_at)}
                          </div>
                          <div className="text-sm text-slate-700">
                            <span className="font-medium text-slate-900">Actual:</span>{" "}
                            {formatMoney(row.actual ?? row.amount)}
                          </div>
                          <div className="text-sm text-slate-700">
                            <span className="font-medium text-slate-900">Approved:</span>{" "}
                            {formatMoney(row.approved)}
                          </div>
                        </div>

                        <div className="mt-2 text-sm text-slate-700">
                          <span className="font-medium text-slate-900">Note:</span>{" "}
                          {row.note || row.description || "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>

            <div className="space-y-6">
              <Card
                title="Management Actions"
                subtitle="Use this dedicated page to perform task actions cleanly."
              >
                <div className="space-y-6">
                  <div className="rounded-2xl border border-slate-200 p-4">
                    <div className="mb-3 text-sm font-semibold text-slate-900">Assign Task</div>
                    <div className="space-y-3">
                      <input
                        type="text"
                        placeholder="Search staff by name, email, position, department..."
                        value={staffSearch}
                        onChange={(e) => setStaffSearch(e.target.value)}
                        className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      />

                      <select
                        value={selectedStaffId}
                        onChange={(e) => {
                          const nextId = e.target.value;
                          setSelectedStaffId(nextId);

                          const found = staffOptions.find(
                            (item) => String(item.staff_id || item.id) === String(nextId)
                          );
                          setSelectedStaffPreview(found || null);
                        }}
                        className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      >
                        <option value="">
                          {staffLoading ? "Loading staff..." : "Select staff member"}
                        </option>
                        {staffOptions.map((staff) => (
                          <option key={staff.staff_id || staff.id} value={staff.staff_id || staff.id}>
                            {buildStaffLabel(staff)}
                          </option>
                        ))}
                      </select>

                      {selectedStaffPreview ? (
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                          <div className="font-semibold text-slate-900">
                            {selectedStaffPreview.full_name || "—"}
                          </div>
                          <div className="mt-1 text-slate-600">
                            {selectedStaffPreview.position || "No position"} •{" "}
                            {selectedStaffPreview.department_name ||
                              selectedStaffPreview.department ||
                              "No department"}
                          </div>
                          <div className="mt-1 text-slate-500">
                            {selectedStaffPreview.email || "No email"}
                          </div>
                        </div>
                      ) : null}

                      <textarea
                        placeholder="Assignment note"
                        value={assignNote}
                        onChange={(e) => setAssignNote(e.target.value)}
                        className="min-h-[88px] w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      />

                      <Button
                        onClick={handleAssign}
                        disabled={actionLoading.assign}
                        className="w-full bg-blue-700 text-white hover:bg-blue-600 disabled:opacity-70"
                      >
                        {actionLoading.assign ? "Saving Assignment..." : "Assign Task"}
                      </Button>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 p-4">
                    <div className="mb-3 text-sm font-semibold text-slate-900">Link Asset</div>
                    <div className="space-y-3">
                      <input
                        type="text"
                        placeholder="Search asset number, name, serial..."
                        value={assetSearch}
                        onChange={(e) => setAssetSearch(e.target.value)}
                        className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                      />

                      <select
                        value={selectedAssetId}
                        onChange={(e) => {
                          const nextId = e.target.value;
                          setSelectedAssetId(nextId);

                          const found = assetResults.find(
                            (item) => String(item.asset_id || item.id) === String(nextId)
                          );
                          setSelectedAssetPreview(found || null);
                        }}
                        className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                      >
                        <option value="">
                          {assetSearchLoading ? "Searching assets..." : "Select asset"}
                        </option>
                        {assetResults.map((asset) => (
                          <option key={asset.asset_id || asset.id} value={asset.asset_id || asset.id}>
                            {asset.asset_number || `Asset #${asset.asset_id || asset.id}`}
                            {asset.asset_name ? ` — ${asset.asset_name}` : ""}
                          </option>
                        ))}
                      </select>

                      {selectedAssetPreview ? (
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                          <div className="font-semibold text-slate-900">
                            {selectedAssetPreview.asset_number || "—"}
                            {selectedAssetPreview.asset_name
                              ? ` — ${selectedAssetPreview.asset_name}`
                              : ""}
                          </div>
                          <div className="mt-1 text-slate-600">
                            {selectedAssetPreview.asset_type || "—"} •{" "}
                            {selectedAssetPreview.location_text ||
                              selectedAssetPreview.location ||
                              "—"}
                          </div>
                        </div>
                      ) : null}

                      <Button
                        onClick={handleLinkAsset}
                        disabled={actionLoading.linkAsset}
                        className="w-full bg-cyan-700 text-white hover:bg-cyan-600 disabled:opacity-70"
                      >
                        {actionLoading.linkAsset ? "Linking Asset..." : "Link Asset to Task"}
                      </Button>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-violet-200 bg-violet-50/40 p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">
                          Maintenance Procurement
                        </div>
                        <div className="mt-1 text-xs text-slate-600">
                          Create a purchase order directly from this task or link an existing one.
                        </div>
                      </div>
                      <Badge className="border border-violet-200 bg-white text-violet-700">
                        {procurementContextLoading ? "Loading..." : "Task-linked PO flow"}
                      </Badge>
                    </div>

                    <div className="space-y-5">
                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <div className="mb-3 text-sm font-semibold text-slate-900">
                          Link Existing Purchase Order
                        </div>
                        <div className="space-y-3">
                          <input
                            type="number"
                            placeholder="Purchase order ID"
                            value={linkPoId}
                            onChange={(e) => setLinkPoId(e.target.value)}
                            className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                          />

                          <Button
                            onClick={handleLinkPO}
                            disabled={actionLoading.procurementLinkPo}
                            className="w-full bg-violet-700 text-white hover:bg-violet-600 disabled:opacity-70"
                          >
                            {actionLoading.procurementLinkPo
                              ? "Linking PO..."
                              : "Link Existing Purchase Order"}
                          </Button>
                        </div>
                      </div>

                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <div className="mb-3 text-sm font-semibold text-slate-900">
                          Create Purchase Order from Task
                        </div>

                        <div className="space-y-3">
                          <select
                            value={procurementVendorId}
                            onChange={(e) => {
                              setProcurementVendorId(e.target.value);
                              setStockResults([]);
                              setStockSearch("");
                            }}
                            className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                          >
                            <option value="">
                              {vendorsLoading ? "Loading vendors..." : "Select vendor"}
                            </option>
                            {vendors.map((vendor) => (
                              <option key={vendor.vendor_id || vendor.id} value={vendor.vendor_id || vendor.id}>
                                {buildVendorLabel(vendor)}
                              </option>
                            ))}
                          </select>

                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <input
                              type="date"
                              value={procurementExpectedDelivery}
                              onChange={(e) => setProcurementExpectedDelivery(e.target.value)}
                              className="h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                            />
                            <input
                              type="number"
                              placeholder="Planner ID (optional)"
                              value={procurementPlannerId}
                              onChange={(e) => setProcurementPlannerId(e.target.value)}
                              className="h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                            />
                          </div>

                          <input
                            type="number"
                            placeholder="Location ID (optional)"
                            value={procurementLocationId}
                            onChange={(e) => setProcurementLocationId(e.target.value)}
                            className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                          />

                          <textarea
                            placeholder="PO notes"
                            value={procurementNotes}
                            onChange={(e) => setProcurementNotes(e.target.value)}
                            className="min-h-[88px] w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                          />

                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Stock Item Search
                            </div>
                            <input
                              type="text"
                              placeholder={
                                procurementVendorId
                                  ? "Search stock item code or description..."
                                  : "Select vendor first"
                              }
                              value={stockSearch}
                              disabled={!procurementVendorId}
                              onChange={(e) => setStockSearch(e.target.value)}
                              className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition disabled:cursor-not-allowed disabled:bg-slate-100 focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                            />

                            {stockSearchLoading ? (
                              <div className="mt-3 text-xs text-slate-500">Searching stock items...</div>
                            ) : stockResults.length > 0 ? (
                              <div className="mt-3 space-y-2">
                                {stockResults.slice(0, 8).map((item) => (
                                  <div
                                    key={item.item_id || item.item_code}
                                    className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-3 sm:flex-row sm:items-center sm:justify-between"
                                  >
                                    <div>
                                      <div className="text-sm font-medium text-slate-900">
                                        {item.item_code || "—"}
                                      </div>
                                      <div className="text-xs text-slate-500">
                                        {item.description || "No description"} •{" "}
                                        {item.unit_size || "No unit size"}
                                      </div>
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                      {poLines.map((line, idx) => (
                                        <Button
                                          key={line.row_id}
                                          type="button"
                                          variant="outline"
                                          className="border-slate-300"
                                          onClick={() => handleApplyStockItemToLine(line.row_id, item)}
                                        >
                                          Add to Line {idx + 1}
                                        </Button>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : stockSearch && procurementVendorId ? (
                              <div className="mt-3 text-xs text-slate-500">No stock items found.</div>
                            ) : null}
                          </div>

                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="text-sm font-semibold text-slate-900">PO Line Items</div>
                              <Button
                                type="button"
                                variant="outline"
                                className="border-slate-300"
                                onClick={handleAddPoLine}
                              >
                                Add Line
                              </Button>
                            </div>

                            {poLines.map((line, idx) => (
                              <div
                                key={line.row_id}
                                className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                              >
                                <div className="mb-3 flex items-center justify-between">
                                  <div className="text-sm font-semibold text-slate-900">
                                    Line {idx + 1}
                                  </div>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    className="border-slate-300"
                                    onClick={() => handleRemovePoLine(line.row_id)}
                                  >
                                    Remove
                                  </Button>
                                </div>

                                <div className="grid grid-cols-1 gap-3">
                                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    <input
                                      type="number"
                                      placeholder="Item ID"
                                      value={line.item_id}
                                      onChange={(e) =>
                                        handlePoLineChange(line.row_id, "item_id", e.target.value)
                                      }
                                      className="h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                                    />
                                    <input
                                      type="text"
                                      placeholder="Item Code"
                                      value={line.item_code}
                                      onChange={(e) =>
                                        handlePoLineChange(line.row_id, "item_code", e.target.value)
                                      }
                                      className="h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                                    />
                                  </div>

                                  <input
                                    type="text"
                                    placeholder="Item Name / Description"
                                    value={line.item_name}
                                    onChange={(e) =>
                                      handlePoLineChange(line.row_id, "item_name", e.target.value)
                                    }
                                    className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                                  />

                                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
                                    <input
                                      type="text"
                                      placeholder="Unit Size"
                                      value={line.unit_size}
                                      onChange={(e) =>
                                        handlePoLineChange(line.row_id, "unit_size", e.target.value)
                                      }
                                      className="h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                                    />
                                    <input
                                      type="number"
                                      step="0.01"
                                      placeholder="Qty"
                                      value={line.quantity}
                                      onChange={(e) =>
                                        handlePoLineChange(line.row_id, "quantity", e.target.value)
                                      }
                                      className="h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                                    />
                                    <input
                                      type="number"
                                      step="0.01"
                                      placeholder="Unit Price"
                                      value={line.unit_price}
                                      onChange={(e) =>
                                        handlePoLineChange(line.row_id, "unit_price", e.target.value)
                                      }
                                      className="h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                                    />
                                    <input
                                      type="number"
                                      step="0.01"
                                      placeholder="Tax %"
                                      value={line.tax_rate}
                                      onChange={(e) =>
                                        handlePoLineChange(line.row_id, "tax_rate", e.target.value)
                                      }
                                      className="h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                                    />
                                  </div>

                                  <div className="text-right text-sm font-semibold text-slate-700">
                                    Line Total: {formatMoney(getLineTotal(line))}
                                  </div>
                                </div>
                              </div>
                            ))}

                            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
                              PO Estimated Total: {formatMoney(procurementPoTotal)}
                            </div>

                            <Button
                              onClick={handleCreatePO}
                              disabled={actionLoading.createPo}
                              className="w-full bg-violet-700 text-white hover:bg-violet-600 disabled:opacity-70"
                            >
                              {actionLoading.createPo
                                ? "Creating Purchase Order..."
                                : "Create Purchase Order from Task"}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 p-4">
                    <div className="mb-3 text-sm font-semibold text-slate-900">Vendor Dispatch</div>
                    <div className="space-y-3">
                      <select
                        value={vendorId}
                        onChange={(e) => setVendorId(e.target.value)}
                        className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
                      >
                        <option value="">
                          {vendorsLoading ? "Loading vendors..." : "Select vendor (optional)"}
                        </option>
                        {vendors.map((vendor) => (
                          <option key={vendor.vendor_id || vendor.id} value={vendor.vendor_id || vendor.id}>
                            {buildVendorLabel(vendor)}
                          </option>
                        ))}
                      </select>

                      <input
                        type="text"
                        placeholder="Callout reference"
                        value={calloutRef}
                        onChange={(e) => setCalloutRef(e.target.value)}
                        className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
                      />

                      <textarea
                        placeholder="Dispatch note"
                        value={vendorDispatchNote}
                        onChange={(e) => setVendorDispatchNote(e.target.value)}
                        className="min-h-[88px] w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
                      />

                      <Button
                        onClick={handleVendorDispatch}
                        disabled={actionLoading.vendorDispatch}
                        className="w-full bg-amber-600 text-white hover:bg-amber-500 disabled:opacity-70"
                      >
                        {actionLoading.vendorDispatch
                          ? "Recording Dispatch..."
                          : "Record Vendor Dispatch"}
                      </Button>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 p-4">
                    <div className="mb-3 text-sm font-semibold text-slate-900">Add Cost Line</div>
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <select
                          value={costForm.source_type}
                          onChange={(e) => handleCostInputChange("source_type", e.target.value)}
                          className="h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                        >
                          {COST_SOURCE_OPTIONS.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          ))}
                        </select>

                        <select
                          value={costForm.cost_type}
                          onChange={(e) => handleCostInputChange("cost_type", e.target.value)}
                          className="h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                        >
                          {COST_TYPE_OPTIONS.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <select
                          value={costForm.vendor_id}
                          onChange={(e) => handleCostInputChange("vendor_id", e.target.value)}
                          className="h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                        >
                          <option value="">Select vendor (optional)</option>
                          {vendors.map((vendor) => (
                            <option key={vendor.vendor_id || vendor.id} value={vendor.vendor_id || vendor.id}>
                              {buildVendorLabel(vendor)}
                            </option>
                          ))}
                        </select>

                        <input
                          type="number"
                          placeholder="PO ID (optional)"
                          value={costForm.po_id}
                          onChange={(e) => handleCostInputChange("po_id", e.target.value)}
                          className="h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                        />
                      </div>

                      <input
                        type="text"
                        placeholder="Cost description"
                        value={costForm.description}
                        onChange={(e) => handleCostInputChange("description", e.target.value)}
                        className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                      />

                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <input
                          type="number"
                          step="0.01"
                          placeholder="Quantity"
                          value={costForm.quantity}
                          onChange={(e) => handleCostInputChange("quantity", e.target.value)}
                          className="h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                        />

                        <input
                          type="number"
                          step="0.01"
                          placeholder="Unit cost"
                          value={costForm.unit_cost}
                          onChange={(e) => handleCostInputChange("unit_cost", e.target.value)}
                          className="h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                        />

                        <input
                          type="number"
                          step="0.01"
                          placeholder="Amount"
                          value={costForm.amount}
                          onChange={(e) => handleCostInputChange("amount", e.target.value)}
                          className="h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                        />
                      </div>

                      <input
                        type="datetime-local"
                        value={costForm.incurred_at}
                        onChange={(e) => handleCostInputChange("incurred_at", e.target.value)}
                        className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                      />

                      <textarea
                        placeholder="Cost note"
                        value={costForm.note}
                        onChange={(e) => handleCostInputChange("note", e.target.value)}
                        className="min-h-[88px] w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                      />

                      <Button
                        onClick={handleAddCostLine}
                        disabled={actionLoading.addCost}
                        className="w-full bg-emerald-700 text-white hover:bg-emerald-600 disabled:opacity-70"
                      >
                        {actionLoading.addCost ? "Adding Cost Line..." : "Add Cost Line"}
                      </Button>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 p-4">
                    <div className="mb-3 text-sm font-semibold text-slate-900">Close Task</div>
                    <div className="space-y-3">
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Final actual cost"
                        value={closeCost}
                        onChange={(e) => setCloseCost(e.target.value)}
                        className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-rose-500 focus:ring-2 focus:ring-rose-100"
                      />

                      <textarea
                        placeholder="Closure note"
                        value={closeNote}
                        onChange={(e) => setCloseNote(e.target.value)}
                        className="min-h-[88px] w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-rose-500 focus:ring-2 focus:ring-rose-100"
                      />

                      <Button
                        onClick={handleCloseTask}
                        disabled={actionLoading.close}
                        className="w-full bg-rose-700 text-white hover:bg-rose-600 disabled:opacity-70"
                      >
                        {actionLoading.close ? "Closing Task..." : "Close Task"}
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>

              <Card
                title="Next Phase Items"
                subtitle="Items still planned after this dedicated task management page."
              >
                <div className="grid grid-cols-1 gap-3">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                    Asset creation workflow from management or dedicated asset register screen
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                    Multi-PO bridge and richer commercial linkage history
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                    Inspection scheduling and asset document workflow
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                    Dedicated asset registry dashboard and maintenance lifecycle reporting
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}